import axios from 'axios';
import Cookies from '../utils/cookies';

const API_URL = process.env.REACT_APP_API_URL || '/api/v1';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 30000,
  withCredentials: true,
});

api.interceptors.request.use(
  (config) => {
    const token = Cookies.get('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const csrfToken = Cookies.get('XSRF-TOKEN');
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const response = await axios.post(`${API_URL}/auth/refresh-token`, {}, { withCredentials: true });

        const { accessToken } = response.data.data;
        Cookies.set('accessToken', accessToken, { secure: window.location.protocol === 'https:', sameSite: 'lax' });

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        Cookies.remove('accessToken');
        window.location.href = '/login?session=expired';
        return Promise.reject(refreshError);
      }
    }

    const errorResponse = {
      message: error.response?.data?.error?.message || 'An unexpected error occurred',
      code: error.response?.data?.error?.code || 'NETWORK_ERROR',
      details: error.response?.data?.error?.details || null,
      status: error.response?.status,
    };

    // 2.10 — Specific error types
    if (!error.response) {
      errorResponse.message = 'Tarmoq xatosi. Internet aloqangizni tekshiring.';
      errorResponse.code = 'NETWORK_ERROR';
    } else if (error.response.status >= 500) {
      errorResponse.message = 'Server xatosi. Keyinroq urinib ko\'ring.';
      errorResponse.code = 'SERVER_ERROR';
    }

    return Promise.reject(errorResponse);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: (data) => api.post('/auth/logout', data),
  refreshToken: (data) => api.post('/auth/refresh-token', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  getMe: () => api.get('/auth/me'),
  updateMe: (data) => api.put('/auth/me', data),
};

export const productsAPI = {
  getAll: (params) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  getBySlug: (slug) => api.get(`/products/slug/${slug}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  getCategories: () => api.get('/products/categories'),
  uploadImages: (files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('images', file));
    const csrfToken = Cookies.get('XSRF-TOKEN');
    return api.post('/products/upload/images', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'X-CSRF-Token': csrfToken
      },
    });
  },
  uploadThumbnail: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    const csrfToken = Cookies.get('XSRF-TOKEN');
    return api.post('/products/upload/thumbnail', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'X-CSRF-Token': csrfToken
      },
    });
  },
  deleteImage: (filename) => api.delete(`/products/upload/${filename}`),
};

export const ordersAPI = {
  create: (data) => api.post('/orders', data),
  getAll: (params) => api.get('/orders', { params }),
  getAllAdmin: (params) => api.get('/orders/all', { params }),
  getById: (id) => api.get(`/orders/${id}`),
  cancel: (id) => api.post(`/orders/${id}/cancel`),
  updateStatus: (id, data) => api.put(`/orders/${id}/status`, data),
  getCart: () => api.get('/orders/cart'),
  addToCart: (data) => api.post('/orders/cart/add', data),
  updateCartItem: (itemId, data) => api.put(`/orders/cart/${itemId}`, data),
  removeFromCart: (itemId) => api.delete(`/orders/cart/${itemId}`),
  clearCart: () => api.delete('/orders/cart'),
};

export const healthAPI = {
  check: () => api.get('/health'),
};

export default api;
